import Breakout from "@/features/Common/components/Breakout";
import DefaultError from "@/features/Common/components/DefaultError";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import PaymentReceipt from "@/features/Common/components/PaymentReceipt";
import {
  getPathologyPaymentRedirectURL,
  Member,
  MemberProductPaymentRatesChargeCode,
  MemberProductPaymentRatesItem,
  PathologyPanel,
} from "@/services/core-api-adapter";
import { Stack, Typography } from "@mui/material";
import { useMachine } from "@xstate/react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import PathologyOrderSummary from "../PathologyOrderSummary";
import PathologyPanelDetails from "../PathologyPanelDetails";
import PathologyPanels from "../PathologyPanels";
import { PathologyPreparePrompt } from "../PathologyPreparePrompt";
import { PathologyPrompt } from "../PathologyPrompt";
import { eventNames, pathologyFlowStateMachine } from "./machine";
import PathologyResultsBreakout from "../PathologyResultsBreakout";
import PathologyPaymentFailure from "../PathologyPaymentFailure";
import { MedicalAssistanceDependantSelection } from "@/features/Common/components/MedicalAssistanceDependantSelection";
import { useGlobalStore } from "@/store";
import { MembershipDependent } from "../../../../services/core-api-adapter";

export default function PathologyFlow() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { state } = useGlobalStore();
  const navigate = useNavigate();

  const panelId = searchParams.get("panelId");
  const orderId = searchParams.get("orderId");

  const [pathologyStateMachine, pathologyStateMachineDispatch] = useMachine(
    pathologyFlowStateMachine,
    {
      input: {
        context: {
          panelId: panelId || null,
          orderId: orderId || null,
          currentUserMemberId: state.currentUser.memberId || null,
        },
      },
    } as any
  );

  function onGoHomeButtonClick() {
    pathologyStateMachineDispatch({ type: eventNames.GO_HOME_BUTTON_CLICKED });
  }

  function onPaymentSuccess() {
    pathologyStateMachineDispatch({
      type: eventNames.PAYMENT_SUCCESS_RECEIVED,
    });
  }

  function onPanelClick(panel: PathologyPanel) {
    pathologyStateMachineDispatch({
      type: eventNames.BLOOD_PANEL_SELECTED,
      output: panel,
    });
  }

  function onBuyNowButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.BUY_NOW_BUTTON_CLICKED,
    });
  }

  function onBackButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.BACK_BUTTON_CLICKED,
    });
  }

  function onViewResultsClick() {
    pathologyStateMachineDispatch({
      type: eventNames.VIEW_RESULTS_BUTTON_CLICKED,
    });
  }

  function onContinueButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
    });
  }

  function onViewPaymentReceiptClick() {
    pathologyStateMachineDispatch({
      type: eventNames.VIEW_RECEIPT_BUTTON_CLICKED,
    });
  }

  function onHowToPrepareClick() {
    pathologyStateMachineDispatch({
      type: eventNames.HOW_TO_PREPARE_BUTTON_CLICKED,
    });
  }

  function onDependentSelectionNextButtonClick(selectedMember: Member) {
    pathologyStateMachineDispatch({
      type: eventNames.DEPENDANT_SELECTED,
      input: selectedMember,
    });
  }

  function onBreakoutDisclaimerContinue() {
    pathologyStateMachineDispatch({
      type: eventNames.USER_REDIRECTED,
    });
  }

  function onStartDoctorConsultationClick() {
    navigate("/home/get-medical-assistance/intercare");
    pathologyStateMachineDispatch({
      type: eventNames.USER_REDIRECTED,
    });
  }

  function getPaymentRedirectUrl() {
    const redirectURL = getPathologyPaymentRedirectURL(
      pathologyStateMachine.context.createOrderResponse?.orderId || ""
    );

    return redirectURL;
  }

  const directToDoctorPaymentRates =
    pathologyStateMachine.context?.memberProfileResponse?.consultation.consultationRates.find(
      (rate: MemberProductPaymentRatesItem) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  function getOrderSummarySelectedDependent(): MembershipDependent | null {
    const memberId =
      pathologyStateMachine.context.fetchOrderDetailsResponse
        ?.testSubjectMemberId;

    const selectedDependent =
      pathologyStateMachine.context.selectedDependent || null;

    if (memberId === state.currentUser.memberId) {
      return {
        memberId: state.currentUser.memberId,
        firstName: state.currentUser.firstName,
        lastName: state.currentUser.lastName,
        identityDocumentType: null,
        identityDocumentValue: null,
        sexAtBirth: "",
        dateOfBirth: "",
        identityDocumentCountryOfIssue: "",
        status: "active",
      };
    }

    if (!selectedDependent) {
      return (
        pathologyStateMachine.context.membersList.find(
          (member) => member.memberId === memberId
        ) || null
      );
    }

    return selectedDependent;
  }

  return (
    <>
      {(pathologyStateMachine.matches("fetchingBloodPanelList") ||
        pathologyStateMachine.matches("creatingOrder") ||
        pathologyStateMachine.matches("waitingForOrderOutcome") ||
        pathologyStateMachine.matches("fetchingOrderDetails") ||
        pathologyStateMachine.matches("processingPaymentOutcome") ||
        pathologyStateMachine.matches("fetchingPaymentReceipt") ||
        pathologyStateMachine.matches("updatingMemberDependents") ||
        pathologyStateMachine.matches("initiatingPayment") ||
        pathologyStateMachine.matches("fetchingMemberProfile") ||
        pathologyStateMachine.matches(
          "fetchingMemberDetailsOrFetchingPaymentReceipt"
        ) ||
        pathologyStateMachine.matches(
          "selectingDependantsFetchingBloodPanelListOrFetchingPaymentReceipt"
        ) ||
        pathologyStateMachine.matches("fetchingBloodPanelDetails")) && (
        <FullscreenLoadingIndicator
          loadingText={t("common.pleaseWait")}
          mainIconType="face"
        />
      )}

      {pathologyStateMachine.matches("showingBloodPanelList") &&
        pathologyStateMachine.context?.memberProfileResponse && (
          <PathologyPanels
            panelList={pathologyStateMachine.context.bloodPanelListResponse}
            onPanelClick={onPanelClick}
            onStartDoctorConsultationClick={onStartDoctorConsultationClick}
            consultAmountFormatted={
              directToDoctorPaymentRates?.amountFormatted || ""
            }
            consultOriginalAmountFormatted={
              directToDoctorPaymentRates?.originalAmountFormatted || ""
            }
            isDiscountApplied={
              directToDoctorPaymentRates?.isDiscountApplied || false
            }
            selectedDependant={
              pathologyStateMachine.context.selectedDependent || null
            }
          />
        )}

      {pathologyStateMachine.matches("selectingDependents") &&
        pathologyStateMachine.context.membersList && (
          <Stack p={2} height="100vh" justifyContent="end">
            <MedicalAssistanceDependantSelection
              onNextButtonClick={onDependentSelectionNextButtonClick}
              dependants={pathologyStateMachine.context.membersList}
            />
          </Stack>
        )}

      {pathologyStateMachine.matches("showingBloodPanelDetails") &&
        pathologyStateMachine.context.selectedBloodPanel && (
          <PathologyPanelDetails
            onBuyButtonClick={onBuyNowButtonClick}
            onBackButtonClick={onBackButtonClick}
            panel={pathologyStateMachine.context.selectedBloodPanel}
            selectedDependant={
              pathologyStateMachine.context.selectedDependent || null
            }
          />
        )}

      {pathologyStateMachine.matches("paymentFailed") && (
        <PathologyPaymentFailure
          onContinue={onGoHomeButtonClick}
          onPaymentSuccess={onPaymentSuccess}
          orderId={
            pathologyStateMachine.context?.orderIdQueryParam ||
            pathologyStateMachine.context?.createOrderResponse?.orderId ||
            ""
          }
        />
      )}

      {pathologyStateMachine.matches("showingPrompt") &&
        pathologyStateMachine.context.selectedBloodPanel && (
          <PathologyPrompt
            panel={pathologyStateMachine.context.selectedBloodPanel}
            onContinueButtonClick={onContinueButtonClick}
            onBackButtonClick={onBackButtonClick}
            selectedDependant={
              pathologyStateMachine.context.selectedDependent || null
            }
          />
        )}

      {pathologyStateMachine.matches("breakoutScreen") && (
        <Stack sx={{ height: "98vh" }}>
          <Breakout
            type="Ecentric"
            onConfirm={onBreakoutDisclaimerContinue}
            redirectUrl={getPaymentRedirectUrl()}
            onDecline={onBackButtonClick}
          />
        </Stack>
      )}

      {pathologyStateMachine.matches("showingOrderSummary") &&
        pathologyStateMachine.context.fetchOrderDetailsResponse &&
        pathologyStateMachine.context.paymentReceiptResponse && (
          <PathologyOrderSummary
            order={pathologyStateMachine.context.fetchOrderDetailsResponse}
            paymentReceipt={
              pathologyStateMachine.context.paymentReceiptResponse
            }
            paymentDate={
              pathologyStateMachine.context.paymentReceiptResponse
                ?.paymentUtcDateTime
            }
            onViewResultsClick={onViewResultsClick}
            onViewReceiptClick={onViewPaymentReceiptClick}
            onBackButtonClick={onBackButtonClick}
            onHowToPrepareClick={onHowToPrepareClick}
            selectedDependant={getOrderSummarySelectedDependent()}
          />
        )}

      {pathologyStateMachine.matches("error") && (
        <Stack height="100vh" justifyContent="end">
          <DefaultError onComeBackLater={onGoHomeButtonClick}>
            <Typography variant="h2">{t("GenericError.title")}</Typography>
            <Typography pb={2}>{t("GenericError.content")}</Typography>
          </DefaultError>
        </Stack>
      )}

      {pathologyStateMachine.matches("showingPaymentReceipt") &&
        pathologyStateMachine.context.fetchOrderDetailsResponse &&
        pathologyStateMachine.context.paymentReceiptResponse && (
          <PaymentReceipt
            amount={
              pathologyStateMachine.context.paymentReceiptResponse.paymentAmount
            }
            currency={
              pathologyStateMachine.context.paymentReceiptResponse.currencyCode
            }
            transactionDate={
              pathologyStateMachine.context.paymentReceiptResponse
                .paymentUtcDateTime
            }
            referenceNumber={
              pathologyStateMachine.context.paymentReceiptResponse
                .transactionReferenceNumber
            }
            merchantId={
              pathologyStateMachine.context.paymentReceiptResponse.unuMerchantId
            }
            cardNumber={
              pathologyStateMachine.context.paymentReceiptResponse.cardNumber
            }
            onBackButtonClick={onBackButtonClick}
            isDiscountApplied={
              pathologyStateMachine.context.paymentReceiptResponse
                .isDiscountApplied
            }
            originalAmount={
              pathologyStateMachine.context.paymentReceiptResponse
                .originalAmount
            }
            selectedPerson={
              pathologyStateMachine.context.selectedDependent || null
            }
          />
        )}

      {pathologyStateMachine.matches("showingHowToPreparePrompt") && (
        <PathologyPreparePrompt onBackButtonClick={onBackButtonClick} />
      )}

      {pathologyStateMachine.matches("showingWhatsappBreakout") && (
        <Stack height="100vh" p={2}>
          <PathologyResultsBreakout onBackButtonClick={onBackButtonClick} />
        </Stack>
      )}

      {pathologyStateMachine.matches("exitToMyPurchases") && (
        <Navigate to="/home/more/my-purchases" />
      )}

      {pathologyStateMachine.matches("exit") && <Navigate to="/" />}
    </>
  );
}
